import { useReducer, useEffect, ChangeEvent } from 'react';
import uuid from 'uuid';
import { uploadImage } from 'shared/graphql/misc/mutations';
import { useStores } from 'src/hooks/use-stores';
import { GqlImageBanner, GqlImageBannerPlatform, Maybe } from 'types/graphql';
import { readFile, imageFileSize } from './image-banner-modal.helper';

type UseImageBannerModalProps = {
  onClose: () => void;
  addBanner: (banner: GqlImageBanner) => void;
  bannerToEdit: GqlImageBanner | null;
};

type UseImageBannerModalReturn = Omit<GqlImageBanner, '_id' | 'position'> & {
  alt: string;
  link: string;
  isEditingBanner: boolean;
  isUploadingImage: boolean;
  isUploadingMobileImage: boolean;
  handleChangeLinkBehavior: (openInNewTab: boolean) => void;
  handleClose: () => void;
  handleFile: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSetImageFromLibrary: (target: 'desktop' | 'mobile', url: string) => void;
  handleSave: () => void;
  handleSetAlt: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSetLink: (event: ChangeEvent<HTMLInputElement>) => void;
  handleSetPlatform: (platform: GqlImageBannerPlatform) => void;
};

type BannerState = Omit<GqlImageBanner, '_id' | 'position'> & {
  isUploadingImage: boolean;
  isUploadingMobileImage: boolean;
  platforms: Maybe<GqlImageBannerPlatform>[] | undefined;
};

export function useImageBannerModal({
  onClose,
  addBanner,
  bannerToEdit,
}: UseImageBannerModalProps): UseImageBannerModalReturn {
  const { apolloClient, UI } = useStores();

  const isEditingBanner = !!bannerToEdit && !!Object.keys(bannerToEdit).length;

  const [event, updateEvent] = useReducer(
    (state: BannerState, action: Partial<BannerState>): BannerState => ({ ...state, ...action }),
    {
      alt: '',
      link: '',
      openInNewTab: true,
      isUploadingImage: false,
      isUploadingMobileImage: false,
      image: '',
      mobileImage: '',
      platforms: [],
    }
  );

  useEffect(() => {
    if (isEditingBanner) {
      updateEvent({
        alt: bannerToEdit.alt ?? '',
        link: bannerToEdit.link ?? '',
        openInNewTab: bannerToEdit.openInNewTab ?? true,
        image: bannerToEdit.image,
        mobileImage: bannerToEdit.mobileImage,
        platforms: bannerToEdit.platforms ?? [],
      });
    } else {
      updateEvent({
        alt: '',
        link: '',
        openInNewTab: true,
        image: '',
        mobileImage: '',
        platforms: [],
      });
    }
  }, [
    bannerToEdit?.alt,
    bannerToEdit?.image,
    bannerToEdit?.link,
    bannerToEdit?.openInNewTab,
    bannerToEdit?.mobileImage,
    bannerToEdit?.platforms,
    isEditingBanner,
  ]);

  function handleSetAlt(e: ChangeEvent<HTMLInputElement>): void {
    updateEvent({ alt: e.target.value });
  }

  function handleSetLink(e: ChangeEvent<HTMLInputElement>): void {
    updateEvent({ link: e.target.value });
  }

  function handleSetPlatform(platform: GqlImageBannerPlatform): void {
    const existingPlatforms = [...(event.platforms ?? [])];
    const existingPlatform = event.platforms?.find((p) => p?.type === platform.type);

    // remove existing platform if it exists
    if (existingPlatform) {
      existingPlatforms.splice(existingPlatforms.indexOf(existingPlatform), 1);
    }

    // add new platform
    existingPlatforms.push(platform);

    updateEvent({ platforms: existingPlatforms });
  }

  function setImageUploading(inputName: string, value: boolean | string): void {
    if (inputName === 'mobile-image-banner' && typeof value === 'boolean') {
      updateEvent({ isUploadingMobileImage: value });
    } else if (inputName === 'mobile-image-banner' && typeof value === 'string') {
      updateEvent({ mobileImage: value });
    } else if (inputName === 'image-banner' && typeof value === 'boolean') {
      updateEvent({ isUploadingImage: value });
    } else if (inputName === 'image-banner' && typeof value === 'string') {
      updateEvent({ image: value });
    }
  }

  function handleFile(e: ChangeEvent<HTMLInputElement>): void {
    const bannerImage = e.currentTarget.files;
    const inputName = e.currentTarget.name;
    const regex = /.(jpg|jpeg|png|gif|webp|avif)$/i;
    if (!bannerImage || !regex.test(bannerImage[0].name)) {
      UI.showErnie('The file you are attempting to upload has unsupported file type.', 'danger');
      return;
    }

    setImageUploading(inputName, true);
    readFile(bannerImage[0], async (readFileEvent) => {
      const approxFileSizeInMbs = imageFileSize(readFileEvent);
      let errorMsg;

      if (inputName.includes('.gif') && approxFileSizeInMbs >= 10) {
        errorMsg = 'The image you are uploading is too large. Please try again with a photo size less than 10MB.';
      }

      if (!errorMsg && approxFileSizeInMbs >= 20) {
        errorMsg = 'The image you are uploading is too large. Please try again with a photo size less than 20MB.';
      }

      if (errorMsg) {
        UI.showErnie(errorMsg, 'danger');
        setImageUploading(inputName, false);
        return;
      }

      let newUrl;
      try {
        if (!readFileEvent.target) {
          return;
        }

        const response = await apolloClient.mutate({
          mutation: uploadImage,
          variables: { input: { image: readFileEvent.target.result } },
        });

        if (response.data.uploadImage.url) {
          newUrl = response.data.uploadImage.url;
        }
      } catch (error) {
        UI.showErnie('Error uploading photo. Please try again.', 'danger');
        console.error(error);
        setImageUploading(inputName, false);
        return;
      }

      setImageUploading(inputName, newUrl);
      setImageUploading(inputName, false);
    });
  }

  function handleSetImageFromLibrary(target: 'desktop' | 'mobile', url: string): void {
    updateEvent(target === 'mobile' ? { mobileImage: url } : { image: url });
  }

  function handleChangeLinkBehavior(openInNewTab: boolean): void {
    updateEvent({ openInNewTab });
  }

  function handleSave(): void {
    const newImageBanner = {
      _id: isEditingBanner && bannerToEdit._id ? bannerToEdit._id : uuid.v4(),
      image: event.image,
      mobileImage: event.mobileImage,
      alt: event.alt,
      link: event.link,
      openInNewTab: event.openInNewTab,
      platforms: event.platforms,
      position: isEditingBanner && bannerToEdit.position ? bannerToEdit.position : 0,
    };

    addBanner(newImageBanner);
    handleClose();
  }

  function handleClose(): void {
    if (!bannerToEdit?._id) {
      updateEvent({
        alt: '',
        link: '',
        isUploadingImage: false,
        isUploadingMobileImage: false,
        image: '',
        mobileImage: '',
        platforms: [],
      });
    }

    onClose();
  }

  return {
    alt: event.alt ?? '',
    link: event.link ?? '',
    openInNewTab: event.openInNewTab,
    isUploadingImage: event.isUploadingImage,
    isUploadingMobileImage: event.isUploadingMobileImage,
    image: event.image,
    mobileImage: event.mobileImage,
    platforms: event.platforms,
    handleSetAlt,
    handleSetLink,
    handleChangeLinkBehavior,
    handleFile,
    handleSetImageFromLibrary,
    handleSave,
    handleSetPlatform,
    handleClose,
    isEditingBanner,
  };
}
